/**
 * UI Text utilities for consistent, reusable copy (delete confirmations, etc.)
 */
import React from 'react';

/**
 * Returns entity label with correct singular/plural: "project" vs "project(s)"
 * @param {string} entity - Base entity name (e.g. 'project', 'test group')
 * @param {number} count - Item count
 * @returns {string} - "entity" when count is 1, "entity(s)" when count > 1
 */
export const getEntityLabel = (entity, count) =>
  count === 1 ? entity : `${entity}(s)`;

/**
 * Returns delete confirmation modal title with correct singular/plural
 * @param {string} entity - Base entity name (e.g. 'project', 'user', 'role')
 * @param {number} count - Number of items being deleted
 * @param {boolean} isMultiAction - True when multiple items selected
 * @returns {string} - Full title string
 */
export const getDeleteConfirmTitle = (entity, count, isMultiAction) => {
  if (!isMultiAction || count <= 1) {
    return `Are you sure you want to delete this ${entity}?`;
  }
  const label = getEntityLabel(entity, count);
  return `Are you sure you want to delete ${count} ${label}?`;
};

/** Default title for delete confirmation modals */
export const getDeleteModalTitle = () => "Confirm Deletion";

/**
 * Returns delete confirmation message with entity in bold (plural only)
 * @param {string} entity - Base entity name (e.g. 'project', 'test case')
 * @param {number} [count] - Number of items being deleted (defaults to 1 if missing or 0)
 * @param {boolean} isMultiAction - True when multiple items selected
 * @param {string} [suffix] - Optional extra text before "This action cannot be undone"
 * @returns {React.ReactNode}
 */
export const getDeleteConfirmMessage = (entity, count, isMultiAction, suffix) => {
  const safeCount = (count != null && count > 0) ? count : 1;
  const isPlural = !(safeCount === 1 && !isMultiAction);
  const subject = isPlural
    ? `${safeCount} ${getEntityLabel(entity, safeCount)}`
    : `this ${entity}`;
  return (
    <>
      You are about to delete {isPlural ? <strong>{subject}</strong> : subject}. {suffix && <>{suffix} </>}This action cannot be undone.
    </>
  );
};
